import { useState } from "react";
import { toast } from "sonner";
import { Send, Copy, Edit3, Languages, ExternalLink, AlertTriangle } from "lucide-react";
import { formatApiError } from "../../lib/api";
import {
  previewCommunication, sendCommunication, CHANNEL_META, COMMUNICATION_TYPE_META,
} from "../../lib/communications";

/**
 * Phase 2I: lightweight, per-action message composer. Thin orchestration
 * over `/communications/preview` + `/communications/send`, the same "call
 * the existing endpoint, don't build a new engine" pattern as
 * `ResolveDepositPanel.jsx`. Not a chat/inbox: one action, one message.
 *
 * Staff-review-before-send is the default everywhere this is used: opening
 * the composer only prepares a message, nothing goes out until "Send".
 */
export default function MessageComposer({ bookingId, communicationType, buttonLabel, onSent }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [channel, setChannel] = useState(null);
  const [language, setLanguage] = useState("en");
  const [body, setBody] = useState("");
  const [lastResult, setLastResult] = useState(null);

  const meta = COMMUNICATION_TYPE_META[communicationType] || { label: communicationType };

  const load = async (ch, lang) => {
    setLoading(true);
    setLastResult(null);
    try {
      const data = await previewCommunication(bookingId, communicationType, { channel: ch, language: lang });
      setPreview(data);
      setChannel(data.channel);
      setLanguage(data.language);
      setBody(data.body);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const openComposer = () => {
    setOpen(true);
    load(null, null);
  };

  const switchLanguage = (lang) => load(channel, lang);
  const switchChannel = (ch) => load(ch, language);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(body);
      toast.success("Message copied");
    } catch {
      toast.error("Could not copy: select and copy the text manually.");
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const res = await sendCommunication(bookingId, communicationType, channel, {
        language, subject: preview?.subject, body,
      });
      setLastResult(res);
      onSent?.(res);
      if (res.outcome === "copy_share") {
        toast.success("Message ready: copy it or open the link below");
      } else if (res.status === "failed") {
        toast.error(res.error_message || "Message failed to send");
      } else {
        toast.success(`${meta.label} sent`);
        setOpen(false);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <button onClick={openComposer} data-testid={`composer-open-${communicationType}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm bg-white border border-slate-300 hover:bg-slate-50">
        <Edit3 className="w-3.5 h-3.5" /> {buttonLabel || meta.label}
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid={`composer-${communicationType}`}>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">{meta.label}</h3>
        <button onClick={() => setOpen(false)} className="text-xs text-slate-500 hover:text-slate-800">Close</button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Preparing message…</div>
      ) : preview && (
        <>
          {preview.missing_required?.length > 0 && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              This booking is missing {preview.missing_required.join(", ")}: fill it in before sending, or edit the message.
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-slate-500 inline-flex items-center gap-1"><Languages className="w-3.5 h-3.5" /> Language</span>
            {["en", "tl"].map((l) => (
              <button key={l} onClick={() => switchLanguage(l)}
                      className={`px-2 py-1 rounded-md border ${language === l ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-white border-slate-300"}`}>
                {l.toUpperCase()}
              </button>
            ))}
            <span className="text-slate-300 px-1">|</span>
            <span className="text-slate-500">Channel</span>
            {Object.keys(CHANNEL_META).map((ch) => (
              <button key={ch} onClick={() => switchChannel(ch)}
                      className={`px-2 py-1 rounded-md border ${channel === ch ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-white border-slate-300"}`}>
                {CHANNEL_META[ch].label}
              </button>
            ))}
          </div>

          {channel === "email" && (
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-slate-500">Subject</span>
              <input value={preview.subject} onChange={() => {}} readOnly
                     className="mt-1 w-full rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm text-slate-600" />
            </label>
          )}
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-slate-500">Message ({preview.template_source === "customized" ? "your template" : "default template"})</span>
            <textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)}
                      data-testid={`composer-body-${communicationType}`}
                      className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
          </label>

          {lastResult?.share_link && (
            <a href={lastResult.share_link} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 text-xs text-[#1B4332] underline">
              <ExternalLink className="w-3.5 h-3.5" /> Open {CHANNEL_META[channel]?.label}
            </a>
          )}

          <div className="flex justify-end gap-2 pt-1">
            {(channel === "whatsapp" || channel === "messenger") && (
              <button onClick={copyToClipboard} data-testid={`composer-copy-${communicationType}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">
                <Copy className="w-3.5 h-3.5" /> Copy message
              </button>
            )}
            <button onClick={send} disabled={sending}
                    data-testid={`composer-send-${communicationType}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#1B4332] text-white hover:opacity-90 text-sm disabled:opacity-50">
              <Send className="w-3.5 h-3.5" />
              {sending ? "Sending…" : (channel === "whatsapp" || channel === "messenger") ? "Mark as copied" : "Send"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
